import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FormControlLabel, FormGroup, Typography } from "@mui/material";
import { Checkbox, Drawer, FormField, TextField, toast } from "@outpave/ds";
import { toastMutationError } from "utils/toastMutationError";
import CardHolderType from "enums/CardHolderType";
import {
  IssuerCardProduct,
  IssuerCardProductUpdate,
} from "hooks/usePlatformIntegrations";
import { CARD_HOLDER_LABEL_KEY } from "utils/platformIntegrations/integrationLabels";
import { Stack } from "components/layout/reusableLayouts";

interface IntegrationCardProgramDrawerProps {
  open: boolean;
  product: IssuerCardProduct | null;
  /** The issuer's brand name, which decides the account type. */
  providerName: string;
  /** Who the issuer can issue cards to; a program offers some of these. */
  supportedCardHolderTypes: CardHolderType[];
  onClose: () => void;
  onSave: (productKey: string, input: IssuerCardProductUpdate) => Promise<void>;
}

/**
 * One card program: the name companies see, the account type the issuer
 * decides, and who companies opening it may issue cards to.
 */
const IntegrationCardProgramDrawer: React.FC<
  IntegrationCardProgramDrawerProps
> = ({
  open,
  product,
  providerName,
  supportedCardHolderTypes,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [cardHolderTypes, setCardHolderTypes] = useState<CardHolderType[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !product) return;
    setName(product.displayName ?? "");
    const offered = product.cardHolderTypes.filter((type) =>
      supportedCardHolderTypes.includes(type),
    );
    setCardHolderTypes(
      supportedCardHolderTypes.length === 1
        ? supportedCardHolderTypes
        : offered,
    );
  }, [open, product, supportedCardHolderTypes]);

  const toggleCardHolderType = (type: CardHolderType) =>
    setCardHolderTypes((current) =>
      current.includes(type)
        ? current.filter((item) => item !== type)
        : supportedCardHolderTypes.filter(
            (item) => item === type || current.includes(item),
          ),
    );

  const handleSubmit = async () => {
    if (!product) return;
    setSubmitting(true);
    try {
      await onSave(product.key, {
        displayName: name.trim(),
        cardHolderTypes,
      });
      toast.success(t("CardProgramSavedToast"));
      onClose();
    } catch (error: unknown) {
      toastMutationError(error, t("Please try again"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      title={t("Edit card program")}
      loading={submitting}
      footer={
        <>
          <Button
            variant="primary-outlined"
            onClick={handleClose}
            disabled={submitting}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary-filled"
            onClick={() => void handleSubmit()}
            disabled={submitting || !name.trim() || !cardHolderTypes.length}
          >
            {t("Save")}
          </Button>
        </>
      }
    >
      <Stack gap="var(--space-4)">
        <TextField
          label={t("Card program name")}
          value={name}
          onChange={(event) => setName(event.target.value)}
          helperText={t("CardProgramNameHelp")}
          required
          fullWidth
        />
        <TextField
          label={t("Account type")}
          value={product?.subAccountType ? t(product.subAccountType) : ""}
          helperText={t("CardProgramAccountTypeHelp", {
            provider: providerName,
          })}
          readOnly
          fullWidth
        />
        {supportedCardHolderTypes.length === 1 ? (
          <TextField
            label={t("Cardholders")}
            value={t(CARD_HOLDER_LABEL_KEY[supportedCardHolderTypes[0]])}
            helperText={t("CardProgramOnlyCardHolderTypeHelp")}
            readOnly
            fullWidth
          />
        ) : (
          <FormField
            asFieldset
            label={t("Cardholders")}
            required
            helperText={t("CardProgramCardHoldersHelp")}
            errorText={
              cardHolderTypes.length
                ? undefined
                : t("CardProgramCardHoldersRequired")
            }
            error={!cardHolderTypes.length}
            fullWidth
          >
            <FormGroup>
              {supportedCardHolderTypes.map((type) => (
                <FormControlLabel
                  key={type}
                  control={
                    <Checkbox
                      checked={cardHolderTypes.includes(type)}
                      onChange={() => toggleCardHolderType(type)}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t(CARD_HOLDER_LABEL_KEY[type])}
                    </Typography>
                  }
                />
              ))}
            </FormGroup>
          </FormField>
        )}
        <TextField
          label={t("IssuersProduct")}
          value={
            product ? `${product.name || product.key} · ${product.key}` : ""
          }
          readOnly
          fullWidth
        />
      </Stack>
    </Drawer>
  );
};

export default IntegrationCardProgramDrawer;
